/**
 * Admin View Manager
 * 
 * Manages navigation between admin views (clips list, clip editor, etc.)
 * Includes URL routing support for browser navigation and deep linking
 */

import logger from '../../core/logger.js';
import { renderClipsListView, registerClipsListCallbacks } from './clips-list.js';
import { renderClipEditor, registerClipEditorCallbacks } from './clip-editor.js';
import { renderPromptsLibraryView, registerPromptsListCallbacks } from './prompts-list.js';
import { renderPromptEditor, registerPromptEditorCallbacks } from './prompt-editor.js';
import { renderModelsRegistryView, registerModelsListCallbacks } from './models-list.js';
import { renderModelEditor, registerModelEditorCallbacks } from './model-editor.js';
import { renderAdminNav, updateActiveNavItem } from './admin-nav.js';
import toast from '../../ui/toast.js';

/**
 * Admin view type
 */
type AdminView = 
  | 'clips-list' 
  | 'clip-editor' 
  | 'create-clip'
  | 'prompts-list'
  | 'prompt-editor'
  | 'create-prompt'
  | 'models-list'
  | 'model-editor'
  | 'create-model';

/**
 * Current view state
 */
let currentView: AdminView = 'clips-list';
let currentClipId: string | null = null;
let currentPromptId: string | null = null;
let currentModelId: string | null = null;

/**
 * Flag to prevent infinite loops when handling popstate events
 */
let isHandlingPopState = false;

/**
 * Store reference to popstate handler for cleanup
 */
let popstateHandler: ((event: PopStateEvent) => void) | null = null;

/**
 * Update browser URL without triggering navigation
 */
function updateURL(path: string): void {
  if (!isHandlingPopState && window.history) {
    const url = path.startsWith('/') ? path : `/${path}`;
    window.history.pushState({ adminPath: path }, '', url);
    logger.debug('admin: URL updated', { path: url });
  }
}

/**
 * Parse URL path and return admin view information
 */
function parseAdminURL(path: string): { view: AdminView; id: string | null } | null {
  // Remove leading slash and any query params
  const cleanPath = path.replace(/^\//, '').split('?')[0];
  
  // Match admin routes
  const adminMatch = cleanPath.match(/^admin(?:\/(.+))?$/);
  if (!adminMatch) {
    return null;
  }
  
  const subPath = adminMatch[1] || '';
  
  // Default to clips list if just /admin
  if (!subPath || subPath === 'clips') {
    return { view: 'clips-list', id: null };
  }
  
  // Clips routes
  if (subPath === 'clips/create') {
    return { view: 'create-clip', id: null };
  }
  
  const clipEditMatch = subPath.match(/^clips\/([^\/]+)\/edit$/);
  if (clipEditMatch) {
    return { view: 'clip-editor', id: clipEditMatch[1] };
  }
  
  // Prompts routes
  if (subPath === 'prompts') {
    return { view: 'prompts-list', id: null };
  }
  
  if (subPath === 'prompts/create') {
    return { view: 'create-prompt', id: null };
  }
  
  const promptEditMatch = subPath.match(/^prompts\/([^\/]+)\/edit$/);
  if (promptEditMatch) {
    return { view: 'prompt-editor', id: promptEditMatch[1] };
  }
  
  // Models routes
  if (subPath === 'models') {
    return { view: 'models-list', id: null };
  }
  
  if (subPath === 'models/create') {
    return { view: 'create-model', id: null };
  }
  
  const modelEditMatch = subPath.match(/^models\/([^\/]+)\/edit$/);
  if (modelEditMatch) {
    return { view: 'model-editor', id: modelEditMatch[1] };
  }
  
  return null;
}

/**
 * Navigate to a view based on URL
 */
function navigateFromURL(path: string): boolean {
  const parsed = parseAdminURL(path);
  if (!parsed) {
    return false;
  }
  
  logger.info('admin: Navigating from URL', { path, view: parsed.view, id: parsed.id });
  
  // Set flag to prevent URL update loop
  isHandlingPopState = true;
  
  try {
    switch (parsed.view) {
      case 'clips-list':
        navigateToClipsList();
        break;
      case 'create-clip':
        navigateToCreateClip();
        break;
      case 'clip-editor':
        if (parsed.id) {
          navigateToClipEditor(parsed.id);
        }
        break;
      case 'prompts-list':
        navigateToPromptsList();
        break;
      case 'create-prompt':
        navigateToCreatePrompt();
        break;
      case 'prompt-editor':
        if (parsed.id) {
          navigateToPromptEditor(parsed.id);
        }
        break;
      case 'models-list':
        navigateToModelsList();
        break;
      case 'create-model':
        navigateToCreateModel();
        break;
      case 'model-editor':
        if (parsed.id) {
          navigateToModelEditor(parsed.id);
        }
        break;
    }
    return true;
  } finally {
    isHandlingPopState = false;
  }
}

/**
 * Initialize admin views
 */
export function initAdminViews(container: HTMLElement): void {
  logger.info('admin: Initializing admin views');
  
  // Clear container to prevent duplicate content
  container.innerHTML = '';
  
  // Reset view state to default (clips list)
  currentView = 'clips-list';
  currentClipId = null;
  currentPromptId = null;
  currentModelId = null;
  
  // Add navigation bar at the top with callbacks
  const nav = renderAdminNav({
    onNavigateToClips: navigateToClipsList,
    onNavigateToPrompts: navigateToPromptsList,
    onNavigateToModels: navigateToModelsList
  });
  container.appendChild(nav);
  
  // Create content container
  const contentContainer = document.createElement('div');
  contentContainer.id = 'admin-content';
  contentContainer.style.padding = '0 1rem';
  container.appendChild(contentContainer);
  
  // Register callbacks
  registerClipsListCallbacks({
    onEditClip: (clipId: string) => {
      navigateToClipEditor(clipId);
    },
    onCreateClip: () => {
      navigateToCreateClip();
    }
  });
  
  registerClipEditorCallbacks({
    onClose: () => {
      navigateToClipsList();
    },
    onSave: (clipId: string) => {
      // Optionally reload the editor or navigate back
      logger.info('admin: Clip saved', { clipId });
    }
  });
  
  registerPromptsListCallbacks({
    onEditPrompt: (promptId: string) => {
      navigateToPromptEditor(promptId);
    },
    onCreatePrompt: () => {
      navigateToCreatePrompt();
    }
  });
  
  registerPromptEditorCallbacks({
    onSave: () => {
      navigateToPromptsList();
    },
    onCancel: () => {
      navigateToPromptsList();
    }
  });
  
  registerModelsListCallbacks({
    onEditModel: (modelId: string) => {
      navigateToModelEditor(modelId);
    },
    onCreateModel: () => {
      navigateToCreateModel();
    }
  });
  
  registerModelEditorCallbacks({
    onSave: () => {
      navigateToModelsList();
    },
    onCancel: () => {
      navigateToModelsList();
    }
  });
  
  // Set up popstate listener for browser back/forward buttons
  const handlePopState = (event: PopStateEvent): void => {
    logger.debug('admin: Handling popstate event', { state: event.state });
    const currentPath = window.location.pathname;
    
    // Only handle admin paths
    if (currentPath.startsWith('/admin')) {
      navigateFromURL(currentPath);
    }
  };
  
  // Remove any existing listener first (if stored)
  if (popstateHandler) {
    window.removeEventListener('popstate', popstateHandler);
  }
  
  // Store and add new listener
  popstateHandler = handlePopState;
  window.addEventListener('popstate', popstateHandler);
  
  // Check current URL and navigate accordingly, or render initial view
  const currentPath = window.location.pathname;
  if (currentPath.startsWith('/admin') && currentPath !== '/admin' && currentPath !== '/admin/') {
    // Navigate based on URL
    const navigated = navigateFromURL(currentPath);
    if (!navigated) {
      // If URL parsing failed, fall back to default view and update URL
      updateURL('/admin');
      renderCurrentView(contentContainer);
    }
  } else {
    // Default view - update URL if needed
    if (!currentPath.startsWith('/admin')) {
      updateURL('/admin');
    }
    renderCurrentView(contentContainer);
  }
}

/**
 * Navigate to clips list
 */
export function navigateToClipsList(): void {
  logger.info('admin: Navigating to clips list');
  currentView = 'clips-list';
  currentClipId = null;
  
  updateActiveNavItem('clips');
  updateURL('/admin/clips');
  
  const container = document.getElementById('admin-content');
  if (container) {
    renderCurrentView(container);
  }
}

/**
 * Navigate to clip editor
 */
export function navigateToClipEditor(clipId: string): void {
  logger.info('admin: Navigating to clip editor', { clipId });
  currentView = 'clip-editor';
  currentClipId = clipId;
  
  updateActiveNavItem('clips');
  updateURL(`/admin/clips/${clipId}/edit`);
  
  const container = document.getElementById('admin-content');
  if (container) {
    renderCurrentView(container);
  }
}

/**
 * Navigate to create clip
 */
export function navigateToCreateClip(): void {
  logger.info('admin: Navigating to create clip');
  currentView = 'create-clip';
  currentClipId = null;
  
  updateActiveNavItem('clips');
  updateURL('/admin/clips/create');
  
  const container = document.getElementById('admin-content');
  if (container) {
    renderCurrentView(container);
  }
}

/**
 * Navigate to prompts list
 */
export function navigateToPromptsList(): void {
  logger.info('admin: Navigating to prompts list');
  currentView = 'prompts-list';
  currentPromptId = null;
  
  updateActiveNavItem('prompts');
  updateURL('/admin/prompts');
  
  const container = document.getElementById('admin-content');
  if (container) {
    renderCurrentView(container);
  }
}

/**
 * Navigate to prompt editor
 */
export function navigateToPromptEditor(promptId: string): void {
  logger.info('admin: Navigating to prompt editor', { promptId });
  currentView = 'prompt-editor';
  currentPromptId = promptId;
  
  updateActiveNavItem('prompts');
  updateURL(`/admin/prompts/${promptId}/edit`);
  
  const container = document.getElementById('admin-content');
  if (container) {
    renderCurrentView(container);
  }
}

/**
 * Navigate to create prompt
 */
export function navigateToCreatePrompt(): void {
  logger.info('admin: Navigating to create prompt');
  currentView = 'create-prompt';
  currentPromptId = null;
  
  updateActiveNavItem('prompts');
  updateURL('/admin/prompts/create');
  
  const container = document.getElementById('admin-content');
  if (container) {
    renderCurrentView(container);
  }
}

/**
 * Navigate to models list
 */
export function navigateToModelsList(): void {
  logger.info('admin: Navigating to models list');
  currentView = 'models-list';
  currentModelId = null;
  
  updateActiveNavItem('models');
  updateURL('/admin/models');
  
  const container = document.getElementById('admin-content');
  if (container) {
    renderCurrentView(container);
  }
}

/**
 * Navigate to model editor
 */
export function navigateToModelEditor(modelId: string): void {
  logger.info('admin: Navigating to model editor', { modelId });
  currentView = 'model-editor';
  currentModelId = modelId;
  
  updateActiveNavItem('models');
  updateURL(`/admin/models/${modelId}/edit`);
  
  const container = document.getElementById('admin-content');
  if (container) {
    renderCurrentView(container);
  }
}

/**
 * Navigate to create model
 */
export function navigateToCreateModel(): void {
  logger.info('admin: Navigating to create model');
  currentView = 'create-model';
  currentModelId = null;
  
  updateActiveNavItem('models');
  updateURL('/admin/models/create');
  
  const container = document.getElementById('admin-content');
  if (container) {
    renderCurrentView(container);
  }
}

/**
 * Render the current view
 */
async function renderCurrentView(container: HTMLElement): Promise<void> {
  switch (currentView) {
    case 'clips-list':
      await renderClipsListView(container);
      break;
    
    case 'clip-editor':
      if (currentClipId) {
        await renderClipEditor(container, currentClipId);
      } else {
        logger.error('admin: No clip ID for editor view');
        await renderClipsListView(container);
      }
      break;
    
    case 'create-clip':
      renderCreateClipView(container);
      break;
    
    case 'prompts-list':
      await renderPromptsLibraryView(container);
      break;
    
    case 'prompt-editor':
      if (currentPromptId) {
        await renderPromptEditor(container, currentPromptId);
      } else {
        logger.error('admin: No prompt ID for editor view');
        await renderPromptsLibraryView(container);
      }
      break;
    
    case 'create-prompt':
      await renderPromptEditor(container);
      break;
    
    case 'models-list':
      await renderModelsRegistryView(container);
      break;
    
    case 'model-editor':
      if (currentModelId) {
        await renderModelEditor(container, currentModelId);
      } else {
        logger.error('admin: No model ID for editor view');
        await renderModelsRegistryView(container);
      }
      break;
    
    case 'create-model':
      await renderModelEditor(container);
      break;
  }
}

/**
 * Render create clip view
 */
function renderCreateClipView(container: HTMLElement): void {
  container.innerHTML = '';
  
  // Create header
  const header = document.createElement('div');
  header.className = 'admin-header';
  header.style.marginBottom = '2rem';
  
  const backBtn = document.createElement('button');
  backBtn.className = 'btn btn--secondary';
  backBtn.textContent = '← Back to List';
  backBtn.style.marginBottom = '1rem';
  backBtn.addEventListener('click', () => navigateToClipsList());
  header.appendChild(backBtn);
  
  const heading = document.createElement('h1');
  heading.className = 'admin-heading';
  heading.textContent = 'Create New Clip';
  header.appendChild(heading);
  
  const subheading = document.createElement('p');
  subheading.className = 'admin-subheading';
  subheading.textContent = 'Create a new clip for the admin workflow';
  subheading.style.color = '#666';
  subheading.style.marginTop = '0.5rem';
  header.appendChild(subheading);
  
  container.appendChild(header);
  
  // Create form
  const form = document.createElement('form');
  form.className = 'clip-create-form';
  form.style.maxWidth = '800px';
  form.style.margin = '0 auto';
  
  // Title
  const titleGroup = createClipFormGroup('Title', 'title', 'text', '', 'e.g., Daily Conversation at a Café');
  form.appendChild(titleGroup);
  
  // Language
  const languageGroup = createClipSelectGroup(
    'Language',
    'language',
    [
      { value: 'tr', label: 'Turkish' },
      { value: 'de', label: 'German' },
      { value: 'en', label: 'English' },
      { value: 'es', label: 'Spanish' },
      { value: 'fr', label: 'French' }
    ],
    'tr'
  );
  form.appendChild(languageGroup);
  
  // Level
  const levelGroup = createClipSelectGroup(
    'CEFR Level',
    'level',
    [
      { value: 'A1', label: 'A1 - Beginner' },
      { value: 'A2', label: 'A2 - Elementary' },
      { value: 'B1', label: 'B1 - Intermediate' },
      { value: 'B2', label: 'B2 - Upper Intermediate' },
      { value: 'C1', label: 'C1 - Advanced' },
      { value: 'C2', label: 'C2 - Proficient' }
    ],
    'B2'
  );
  form.appendChild(levelGroup);
  
  // Format
  const formatGroup = createClipSelectGroup(
    'Format',
    'format',
    [
      { value: 'monologue', label: 'Monologue (Single Speaker)' },
      { value: 'dialogue', label: 'Dialogue (Multiple Speakers)' }
    ],
    'monologue'
  );
  form.appendChild(formatGroup);
  
  // Script (optional)
  const scriptGroup = createClipTextareaGroup(
    'Script (Optional)',
    'script',
    '',
    'Enter the script text here... You can add this later in the editor.'
  );
  form.appendChild(scriptGroup);
  
  // Submit button
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'btn btn--primary';
  submitBtn.textContent = 'Create Clip';
  submitBtn.style.marginTop = '1.5rem';
  form.appendChild(submitBtn);
  
  // Handle form submission
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    
    const formData = new FormData(form);
    const clipData: import('../../types/admin-clip.js').CreateClipRequest = {
      title: formData.get('title') as string,
      language: formData.get('language') as 'tr' | 'de' | 'en',
      level: formData.get('level') as 'B2' | 'B1',
      format: formData.get('format') as 'dialogue' | 'monologue' | 'mixed',
      script: formData.get('script') as string || undefined,
      questions: []
    };
    
    // Validate required fields
    if (!clipData.title || !clipData.language || !clipData.level || !clipData.format) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Disable submit button
    submitBtn.disabled = true;
    submitBtn.textContent = 'Creating...';
    
    try {
      logger.info('admin: Creating clip', clipData);
      
      // Import AdminClipsService
      const { adminClipsService } = await import('../../services/admin-clips-service.js');
      const newClip = await adminClipsService.createClip(clipData);
      
      logger.info('admin: Clip created successfully', { id: newClip.id });
      
      // Show success toast
      toast.success('Clip created successfully!');
      
      // Navigate to the editor for the new clip
      navigateToClipEditor(newClip.id);
    } catch (error) {
      logger.error('admin: Failed to create clip', { error });
      toast.error(`Failed to create clip: ${error instanceof Error ? error.message : 'Unknown error'}`);
      
      // Re-enable button
      submitBtn.disabled = false;
      submitBtn.textContent = 'Create Clip';
    }
  });
  
  container.appendChild(form);
}

/**
 * Helper: Create form group for clip form
 */
function createClipFormGroup(
  label: string,
  name: string,
  type: string,
  value: string,
  placeholder?: string
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'form-group';
  group.style.marginBottom = '1.5rem';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'form-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `clip-${name}`;
  labelEl.style.display = 'block';
  labelEl.style.marginBottom = '0.5rem';
  labelEl.style.fontWeight = '600';
  group.appendChild(labelEl);
  
  const input = document.createElement('input');
  input.type = type;
  input.id = `clip-${name}`;
  input.name = name;
  input.className = 'form-input';
  input.value = value;
  if (placeholder) input.placeholder = placeholder;
  input.required = true;
  input.style.width = '100%';
  input.style.padding = '0.5rem';
  input.style.border = '1px solid #ccc';
  input.style.borderRadius = '4px';
  group.appendChild(input);
  
  return group;
}

/**
 * Helper: Create select group for clip form
 */
function createClipSelectGroup(
  label: string,
  name: string,
  options: Array<{ value: string; label: string }>,
  defaultValue: string
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'form-group';
  group.style.marginBottom = '1.5rem';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'form-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `clip-${name}`;
  labelEl.style.display = 'block';
  labelEl.style.marginBottom = '0.5rem';
  labelEl.style.fontWeight = '600';
  group.appendChild(labelEl);
  
  const select = document.createElement('select');
  select.id = `clip-${name}`;
  select.name = name;
  select.className = 'form-select';
  select.required = true;
  select.style.width = '100%';
  select.style.padding = '0.5rem';
  select.style.border = '1px solid #ccc';
  select.style.borderRadius = '4px';
  
  options.forEach(opt => {
    const option = document.createElement('option');
    option.value = opt.value;
    option.textContent = opt.label;
    if (opt.value === defaultValue) {
      option.selected = true;
    }
    select.appendChild(option);
  });
  
  group.appendChild(select);
  return group;
}

/**
 * Helper: Create textarea group for clip form
 */
function createClipTextareaGroup(
  label: string,
  name: string,
  value: string,
  placeholder?: string
): HTMLElement {
  const group = document.createElement('div');
  group.className = 'form-group';
  group.style.marginBottom = '1.5rem';
  
  const labelEl = document.createElement('label');
  labelEl.className = 'form-label';
  labelEl.textContent = label;
  labelEl.htmlFor = `clip-${name}`;
  labelEl.style.display = 'block';
  labelEl.style.marginBottom = '0.5rem';
  labelEl.style.fontWeight = '600';
  group.appendChild(labelEl);
  
  const textarea = document.createElement('textarea');
  textarea.id = `clip-${name}`;
  textarea.name = name;
  textarea.className = 'form-textarea';
  textarea.value = value;
  if (placeholder) textarea.placeholder = placeholder;
  textarea.rows = 6;
  textarea.style.width = '100%';
  textarea.style.padding = '0.5rem';
  textarea.style.border = '1px solid #ccc';
  textarea.style.borderRadius = '4px';
  textarea.style.fontFamily = 'inherit';
  group.appendChild(textarea);
  
  return group;
}

/**
 * Get current view
 */
export function getCurrentView(): AdminView {
  return currentView;
}
